import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

/* ============================================================
   StorefrontMockup — tilted, auto-animating Shopify PDP
   Cycles through 4 real case-study products. Cart drawer
   briefly opens each cycle. 3 hand-drawn annotations point
   at craft details.

   Used in: /shopify-experts-development hero (right column).
============================================================ */

const PRODUCTS = [
  {
    brand: "Ming Yu Wang",
    name: "Hammered Silver Band",
    price: "$ 348",
    note: "Recycled sterling · New York",
    img: "https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?auto=format&fit=crop&w=1400&q=80",
    tone: "#F7EFDE", // warm paper
  },
  {
    brand: "Teddy Blake",
    name: "Soft Leather Tote",
    price: "$ 695",
    note: "Florentine calfskin · Italy",
    img: "https://images.unsplash.com/photo-1591561954557-26941169b49e?auto=format&fit=crop&w=1400&q=80",
    tone: "#EFE2D4",
  },
  {
    brand: "Sclarandis",
    name: "Low-Heel Courier",
    price: "$ 420",
    note: "Made-in-Italy · Spring&nbsp;25",
    img: "https://images.unsplash.com/photo-1543163521-1bf539c55dd2?auto=format&fit=crop&w=1400&q=80",
    tone: "#EBE4DA",
  },
  {
    brand: "Rose Forever",
    name: "Paris Bouquet — 24 Stems",
    price: "$ 220",
    note: "Preserved · Ships worldwide",
    img: "https://images.unsplash.com/photo-1487530811176-3780de880c2d?auto=format&fit=crop&w=1400&q=80",
    tone: "#F4E4E4",
  },
];

const CYCLE_MS = 3600; // swap product every 3.6s
const DRAWER_OPEN_AT = 2200; // trigger drawer at +2.2s
const DRAWER_CLOSE_AT = 3200; // close at +3.2s

/* ---------- tiny sub-parts ---------- */

const BrowserChrome = ({ host = "sundaydigital.io/store" }) => (
  <div className="flex items-center gap-3 h-10 px-4 border-b border-black/[0.06] bg-[#F7F6F3]">
    <div className="flex items-center gap-1.5">
      <span className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
      <span className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
      <span className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
    </div>
    <div className="flex-1 flex justify-center">
      <div className="inline-flex items-center gap-2 max-w-[70%] px-3 py-1 rounded-md bg-white/80 border border-black/[0.05] text-[0.68rem] tracking-[0.01em] text-neutral-500 font-medium truncate">
        <svg viewBox="0 0 24 24" width="10" height="10" className="text-neutral-400 shrink-0">
          <path fill="currentColor" d="M17 8h-1V6a4 4 0 0 0-8 0v2H7a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V9a1 1 0 0 0-1-1Zm-7-2a2 2 0 1 1 4 0v2h-4Z" />
        </svg>
        {host}
      </div>
    </div>
    <div className="flex items-center gap-1.5 opacity-60">
      <span className="w-1 h-1 rounded-full bg-neutral-500" />
      <span className="w-1 h-1 rounded-full bg-neutral-500" />
      <span className="w-1 h-1 rounded-full bg-neutral-500" />
    </div>
  </div>
);

const StoreNav = () => (
  <div className="flex items-center justify-between px-5 py-3 border-b border-black/[0.05] bg-white">
    <span className="font-display text-[0.82rem] font-black tracking-[-0.01em] text-neutral-900">
      MING<span className="text-[#F4D64E]">·</span>YU
    </span>
    <div className="hidden sm:flex items-center gap-5 text-[0.6rem] font-bold tracking-[0.22em] uppercase text-neutral-500">
      <span>Shop</span>
      <span>Journal</span>
      <span>Studio</span>
    </div>
    <div className="flex items-center gap-3 text-neutral-500">
      <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.8">
        <circle cx="11" cy="11" r="7" /><path d="m21 21-4-4" />
      </svg>
      <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.8">
        <path d="M6 6h15l-2 11H8L6 6Z" /><path d="M6 6 5 3H2" /><circle cx="10" cy="20" r="1" /><circle cx="18" cy="20" r="1" />
      </svg>
    </div>
  </div>
);

const CartDrawer = ({ open, product }) => (
  <AnimatePresence>
    {open ? (
      <motion.div
        key="drawer"
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        exit={{ x: "100%" }}
        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        className="absolute top-0 right-0 bottom-0 w-[62%] bg-white border-l border-black/[0.06] shadow-[0_40px_120px_-30px_rgba(0,0,0,0.25)] z-30"
      >
        <div className="h-10 flex items-center justify-between px-4 border-b border-black/[0.05] bg-[#FAF8F3]">
          <span className="text-[0.6rem] font-bold tracking-[0.22em] uppercase text-neutral-700">
            Your cart · 1
          </span>
          <span className="text-[0.7rem] text-neutral-400">×</span>
        </div>
        <div className="p-4 flex items-center gap-3">
          <div
            className="w-12 h-14 rounded-md bg-cover bg-center border border-black/[0.05]"
            style={{ backgroundImage: `url(${product.img})` }}
          />
          <div className="flex-1 min-w-0">
            <div className="text-[0.7rem] font-bold tracking-[-0.005em] text-neutral-900 truncate">
              {product.name}
            </div>
            <div className="text-[0.6rem] text-neutral-500 mt-0.5 tabular-nums">
              {product.price}
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-black/[0.05] bg-[#FAF8F3]">
          <div className="flex items-center justify-between mb-3">
            <span className="text-[0.65rem] font-bold tracking-[0.14em] uppercase text-neutral-500">
              Subtotal
            </span>
            <span className="text-[0.8rem] font-bold text-neutral-900 tabular-nums">
              {product.price}
            </span>
          </div>
          <div className="w-full h-7 bg-neutral-900 rounded-md flex items-center justify-center text-[0.6rem] font-bold tracking-[0.2em] uppercase text-white">
            Checkout
          </div>
        </div>
      </motion.div>
    ) : null}
  </AnimatePresence>
);

/* ---------- hand-drawn annotation arrow ---------- */
const Annotation = ({ label, style, path, textAlign = "left" }) => (
  <div
    className="absolute pointer-events-none select-none"
    style={style}
    aria-hidden
  >
    <svg
      viewBox="0 0 120 70"
      className="absolute"
      width="120"
      height="70"
      style={{
        top: path.svgTop,
        left: path.svgLeft,
        transform: path.svgTransform,
      }}
    >
      <path
        d={path.d}
        stroke="#F4D64E"
        strokeWidth="1.6"
        fill="none"
        strokeLinecap="round"
        strokeDasharray="2 4"
      />
      <path
        d={path.arrowD}
        stroke="#F4D64E"
        strokeWidth="1.6"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
    <div
      className={`text-[0.65rem] font-bold tracking-[0.14em] uppercase text-[#F4D64E] leading-[1.35] max-w-[14ch] ${
        textAlign === "right" ? "text-right" : "text-left"
      }`}
      style={{ fontFamily: "inherit" }}
    >
      {label}
    </div>
  </div>
);

/* ---------- the full mockup ---------- */
export default function StorefrontMockup() {
  const [index, setIndex] = useState(0);
  const [cartOpen, setCartOpen] = useState(false);
  const [pulseCta, setPulseCta] = useState(false);

  const product = PRODUCTS[index];

  // Cycle products on a timer + trigger drawer mid-cycle
  useEffect(() => {
    const cycle = setInterval(() => {
      setIndex((i) => (i + 1) % PRODUCTS.length);
    }, CYCLE_MS);
    const openT = setInterval(() => {
      setPulseCta(true);
      setTimeout(() => setPulseCta(false), 280);
      setTimeout(() => setCartOpen(true), DRAWER_OPEN_AT - DRAWER_OPEN_AT + 50);
      setTimeout(() => setCartOpen(false), DRAWER_CLOSE_AT - DRAWER_OPEN_AT + 50);
    }, CYCLE_MS);
    return () => {
      clearInterval(cycle);
      clearInterval(openT);
    };
  }, []);

  const annotations = useMemo(
    () => [
      {
        label: "Custom Liquid. 0 apps.",
        style: { top: "-4%", left: "-18%", width: "11rem" },
        textAlign: "right",
        path: {
          svgTop: 24,
          svgLeft: 96,
          svgTransform: "none",
          d: "M 6 6 C 40 18 70 38 112 62",
          arrowD: "M 104 56 L 114 64 L 102 66",
        },
      },
      {
        label: "98 Lighthouse. No JS bloat.",
        style: { top: "42%", right: "-22%", width: "13rem" },
        textAlign: "left",
        path: {
          svgTop: -2,
          svgLeft: -68,
          svgTransform: "none",
          d: "M 112 26 C 80 18 50 22 12 30",
          arrowD: "M 22 24 L 10 30 L 18 38",
        },
      },
      {
        label: "Drawer opens in <120ms.",
        style: { bottom: "-26%", left: "20%", width: "13rem" },
        textAlign: "left",
        path: {
          svgTop: -56,
          svgLeft: 4,
          svgTransform: "none",
          d: "M 18 6 C 40 34 62 44 92 58",
          arrowD: "M 84 52 L 94 60 L 84 62",
        },
      },
    ],
    []
  );

  return (
    <div
      data-testid="dev-hero-mockup"
      className="relative w-full pt-10 sm:pt-14 md:pt-16 pb-20 md:pb-24"
      style={{ perspective: "1600px" }}
    >
      {/* Tilted card wrapper */}
      <motion.div
        className="relative mx-auto w-full max-w-[420px] lg:max-w-[460px]"
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        style={{
          transform: "rotateY(-7deg) rotateX(3deg)",
          transformStyle: "preserve-3d",
        }}
      >
        {/* Floor shadow */}
        <div
          aria-hidden
          className="absolute -bottom-10 left-[8%] right-[8%] h-10 rounded-[50%] blur-2xl bg-black/50"
        />

        {/* Outer frame */}
        <div
          className="relative rounded-[14px] overflow-hidden bg-white border border-white/[0.10] shadow-[0_40px_100px_-25px_rgba(0,0,0,0.7),0_20px_40px_-15px_rgba(244,214,78,0.12)]"
          style={{ transformStyle: "preserve-3d" }}
        >
          <BrowserChrome />
          <StoreNav />

          {/* PDP viewport */}
          <div className="relative aspect-[4/5] bg-[#FAF8F3] overflow-hidden">
            {/* Hero image (crossfades) */}
            <AnimatePresence mode="popLayout">
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 1.04 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 1 }}
                transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                className="absolute inset-0"
                style={{ backgroundColor: product.tone }}
              >
                <img
                  src={product.img}
                  alt={`${product.brand} — ${product.name}`}
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <span
                  aria-hidden
                  className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/20"
                />
              </motion.div>
            </AnimatePresence>

            {/* Thumbnail strip */}
            <div className="absolute top-4 left-4 flex flex-col gap-2 z-10">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className={`w-8 h-10 rounded-md border overflow-hidden transition-opacity ${
                    i === 0
                      ? "border-neutral-900/80"
                      : "border-white/60 opacity-80"
                  }`}
                >
                  <div
                    className="w-full h-full bg-cover bg-center"
                    style={{
                      backgroundImage: `url(${
                        PRODUCTS[(index + i) % PRODUCTS.length].img
                      })`,
                      filter: i === 0 ? "none" : "brightness(1.1)",
                    }}
                  />
                </div>
              ))}
            </div>

            {/* Product info block (bottom) */}
            <div className="absolute bottom-0 left-0 right-0 p-5 bg-gradient-to-t from-white via-white/95 to-white/0 pt-10 z-10">
              <AnimatePresence mode="popLayout">
                <motion.div
                  key={`info-${index}`}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{
                    duration: 0.55,
                    ease: [0.22, 1, 0.36, 1],
                    delay: 0.15,
                  }}
                >
                  <div className="text-[0.6rem] font-bold tracking-[0.26em] uppercase text-neutral-500">
                    {product.brand}
                  </div>
                  <div className="mt-1.5 font-display text-[0.98rem] font-black tracking-[-0.015em] leading-[1.2] text-neutral-900">
                    {product.name}
                  </div>
                  <div className="mt-1 flex items-baseline gap-3 text-[0.72rem]">
                    <span
                      className="font-bold text-neutral-900 tabular-nums"
                      dangerouslySetInnerHTML={{ __html: product.price }}
                    />
                    <span
                      className="text-neutral-500"
                      dangerouslySetInnerHTML={{ __html: product.note }}
                    />
                  </div>
                </motion.div>
              </AnimatePresence>

              <motion.button
                type="button"
                aria-label="Add to cart"
                animate={
                  pulseCta
                    ? { scale: 0.97, backgroundColor: "#F4D64E", color: "#0B0F14" }
                    : { scale: 1, backgroundColor: "#0B0F14", color: "#FFFFFF" }
                }
                transition={{ duration: 0.18, ease: "easeOut" }}
                className="mt-4 w-full h-10 rounded-md flex items-center justify-center text-[0.62rem] font-bold tracking-[0.24em] uppercase"
              >
                Add to cart
              </motion.button>
            </div>

            <CartDrawer open={cartOpen} product={product} />
          </div>
        </div>

        {/* Soft yellow ambient glow behind */}
        <span
          aria-hidden
          className="absolute -top-10 -right-10 w-48 h-48 rounded-full bg-[#F4D64E]/[0.18] blur-[70px] -z-10"
        />
        <span
          aria-hidden
          className="absolute -bottom-16 -left-10 w-56 h-56 rounded-full bg-[#F4D64E]/[0.08] blur-[90px] -z-10"
        />

        {/* Hand-drawn annotations */}
        {annotations.map((a, i) => (
          <Annotation
            key={i}
            label={a.label}
            style={a.style}
            path={a.path}
            textAlign={a.textAlign}
          />
        ))}
      </motion.div>

      {/* Bottom signature */}
      <div className="mt-20 md:mt-24 flex items-center justify-center gap-4 text-[0.62rem] font-bold tracking-[0.28em] uppercase text-neutral-500">
        <span className="w-8 h-px bg-[#F4D64E]" />
        <span>Live demo · 4 real client products</span>
        <span className="w-8 h-px bg-[#F4D64E]" />
      </div>
    </div>
  );
}
